import React, { useState } from 'react'
import { Shield, ChevronDown } from 'lucide-react'
import { UserRole } from '@/types/auth'
import { Z_INDEX } from '@/lib/constants/z-index'

interface AdminRolePickerProps {
  currentRole: UserRole
  onRoleChange: (role: UserRole) => void
}

const AVAILABLE_ROLES: { value: UserRole; label: string; description: string }[] = [
  { value: 'admin', label: 'Admin', description: 'Acesso total ao sistema' },
  { value: 'ceo', label: 'CEO', description: 'Diretor Executivo' },
  { value: 'vice-presidente', label: 'Vice Presidente', description: 'Vice Presidente Executivo' },
  { value: 'diretor-executivo', label: 'Diretor Executivo', description: 'Acesso de nível diretor' },
  { value: 'diretor-financeiro', label: 'Diretor Financeiro', description: 'Diretor Executivo + Big Numbers' },
  { value: 'diretor-tecnico', label: 'Diretor Técnico', description: 'Diretor Técnico' },
  { value: 'coordenador', label: 'Coordenador', description: 'Acesso de coordenador' },
  { value: 'rh', label: 'RH', description: 'Recursos Humanos' },
  { value: 'analista-rh', label: 'Analista RH', description: 'Analista de RH' },
  { value: 'employee', label: 'Funcionário', description: 'Acesso padrão de funcionário' },
]

export function AdminRolePicker({ currentRole, onRoleChange }: AdminRolePickerProps) {
  const [isOpen, setIsOpen] = useState(false)

  const currentRoleInfo = AVAILABLE_ROLES.find(r => r.value === currentRole) || AVAILABLE_ROLES[0]

  const handleRoleSelect = (role: UserRole) => {
    onRoleChange(role)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-brand-dark-light hover:bg-brand-dark-lighter rounded-lg transition-colors text-xs"
      >
        <Shield className="w-4 h-4 text-brand-primary" />
        <span className="text-white font-medium">Papel: {currentRoleInfo.label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0"
            style={{ zIndex: Z_INDEX.BACKDROP }}
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div
            className="absolute top-full right-0 mt-2 w-64 bg-brand-dark-light border border-gray-700 rounded-lg shadow-xl"
            style={{ zIndex: Z_INDEX.DROPDOWN }}
          >
            <div className="p-2">
              <div className="px-3 py-2 text-xs text-brand-gray border-b border-gray-700 mb-2">
                Substituir Papel (Somente Admin)
              </div>
              {AVAILABLE_ROLES.map((role) => (
                <button
                  key={role.value}
                  onClick={() => handleRoleSelect(role.value)}
                  className={`w-full text-left px-3 py-2 rounded transition-colors ${
                    currentRole === role.value
                      ? 'bg-brand-primary/20 text-brand-primary'
                      : 'hover:bg-brand-dark-lighter text-white'
                  }`}
                >
                  <div className="font-medium text-sm">{role.label}</div>
                  <div className="text-xs text-brand-gray">{role.description}</div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default AdminRolePicker
